// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Reading the expedited eligibility dataset: one `determination` verdict plus a per-criterion
 * audit trail. Current results report every criterion with evidence. Earlier
 * results reported only PRESENT and UNCLEAR criteria, so missing entries still
 * mean ABSENT when those stored results are rendered.
 *
 * Shared by the dashboard renderer and the report export so the printed review is the same
 * artifact the analyst signed off on. Both surfaces overlay reported rows onto the workspace's
 * static KB list, which keeps the checklist complete for current results and earlier results
 * that stored only criteria the model flagged.
 */
import type { NodeApiClient } from '@/app-shell/services'

export type CriterionStatus = 'PRESENT' | 'ABSENT' | 'UNCLEAR'

export interface EligibilityCriterion {
  knowledgeItem: string
  status: CriterionStatus
  reason: string | null
  recommendation: string | null
}

export interface Determination {
  verdict: string
  recommendation: string | null
  /** Plain-English explanation of what the decision turned on, citing criterion ids.
   *  Null on results stored before the schema required it. */
  rationale: string | null
  decisiveText: string | null
  sectionReference: string | null
  pageNumber: number | null
}

/** The only verdict that clears a request for the expedited path. */
export const ELIGIBLE_VERDICT = 'EXPEDITED-ELIGIBLE'

/** A dataset read, from either fetch path — only the payload matters here. */
interface DatasetLike {
  payload?: unknown
}

interface EligibilityPayload {
  determination?: Record<string, unknown>
  criteria?: Array<Record<string, unknown>>
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null)

const payloadOf = (dataset: DatasetLike | null | undefined): EligibilityPayload =>
  (dataset?.payload as EligibilityPayload | undefined) ?? {}

/**
 * Whether a dataset read carries an eligibility determination rather than a findings list.
 *
 * A track's datasets are read by key without knowing which half of the review each one holds,
 * so the payload's own shape is what tells them apart.
 */
export function isEligibilityPayload(dataset: DatasetLike | null | undefined): boolean {
  const data = payloadOf(dataset)
  return data.determination != null || Array.isArray(data.criteria)
}

function parseStatus(v: unknown): CriterionStatus {
  const s = typeof v === 'string' ? v.toUpperCase() : ''
  return s === 'PRESENT' || s === 'UNCLEAR' ? s : 'ABSENT'
}

export function parseDetermination(dataset: DatasetLike | null | undefined): Determination | null {
  const d = payloadOf(dataset).determination
  if (!d) return null
  return {
    verdict: str(d.verdict) ?? 'INSUFFICIENT INFORMATION',
    recommendation: str(d.recommendation),
    rationale: str(d.rationale),
    decisiveText: str(d.decisiveText) ?? str(d.decisive_text),
    sectionReference: str(d.sectionReference) ?? str(d.section_reference),
    pageNumber: num(d.pageNumber) ?? num(d.page_number),
  }
}

/**
 * The criterion's leading code (e.g. "E02"), used to match the model's flagged entries to the
 * static KB list regardless of label/punctuation drift.
 */
export function criterionCode(s: string): string {
  const m = /\s*(E\d+)/i.exec(s)
  return m ? m[1].toUpperCase() : s.trim().toLowerCase()
}

/** A criterion from the static KB list: its match code and display title. */
export interface KbCriterion {
  code: string
  title: string
}

/** Reported criteria, keyed by criterion code. Old results contain only PRESENT/UNCLEAR rows. */
export function parseCriteria(dataset: DatasetLike | null | undefined): Map<string, EligibilityCriterion> {
  const criteria = new Map<string, EligibilityCriterion>()
  for (const f of payloadOf(dataset).criteria ?? []) {
    const knowledgeItem = str(f.knowledgeItem) ?? str(f.knowledge_item) ?? 'Criterion'
    const code = criterionCode(knowledgeItem)
    if (criteria.has(code)) continue
    criteria.set(code, {
      knowledgeItem,
      status: parseStatus(f.status),
      // Earlier stored results used `note` before the schema renamed it to `reason`.
      reason: str(f.reason) ?? str(f.note),
      recommendation: str(f.recommendation),
    })
  }
  return criteria
}

/**
 * Overlay reported criteria onto the static KB list. Current results supply every row. For
 * stored flagged-only results, an omitted row keeps the former ABSENT interpretation.
 */
export function buildChecklist(
  kbCriteria: KbCriterion[],
  reported: Map<string, EligibilityCriterion>,
): EligibilityCriterion[] {
  return kbCriteria.map(({ code, title }) => {
    const hit = reported.get(code)
    return hit
      ? { ...hit, knowledgeItem: title }
      : {
          knowledgeItem: title,
          status: 'ABSENT' as const,
          reason: null,
          recommendation: null,
        }
  })
}

/**
 * Banner summary of the audit trail. A clean result reads "All 14 criteria absent" — the
 * literal counts ("0 present · 0 unclear") only say what was NOT found, which reads as if
 * nothing was reviewed.
 */
export function criteriaSummary(criteria: EligibilityCriterion[]): string {
  const present = criteria.filter((c) => c.status === 'PRESENT').length
  const unclear = criteria.filter((c) => c.status === 'UNCLEAR').length
  if (criteria.length > 0 && present === 0 && unclear === 0) {
    return `All ${criteria.length} criteria absent`
  }
  return `${present} present · ${unclear} unclear`
}

/** Where a quoted span came from: "record.price · Page 1", or "" if neither is known. */
export function citation(determination: Determination): string {
  return [
    determination.sectionReference,
    determination.pageNumber != null ? `Page ${determination.pageNumber}` : null,
  ]
    .filter((p): p is string => Boolean(p))
    .join(' · ')
}

/** Triggered/unclear criteria first, then absent — the audit trail in decision-relevant order. */
export function byDecisionRelevance(criteria: EligibilityCriterion[]): EligibilityCriterion[] {
  const order: Record<CriterionStatus, number> = { PRESENT: 0, UNCLEAR: 1, ABSENT: 2 }
  return [...criteria].sort((a, b) => order[a.status] - order[b.status])
}

/** Display title for a KB item: its first content heading (e.g. "E01 — Cost Threshold"),
 *  falling back to the raw id. */
function titleFromContent(content: string, fallback: string): string {
  const first = content.split('\n').map((l) => l.trim()).find((l) => l.length > 0) ?? ''
  return first.replace(/^#+\s*/, '').trim() || fallback
}

/** The workspace's static criteria list (the KB item ids), in KB order. */
export async function fetchKbCriteria(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  kbInstanceId: string,
): Promise<KbCriterion[]> {
  const url = `/org/${orgId}/ws/${workspaceId}/knowledge_base/instances/${kbInstanceId}/knowledge-base`
  const res = await apiClient.get<{ items?: Array<{ id: string; content?: string }> } | null>(url)
  return (res.data?.items ?? []).map((i) => ({
    code: criterionCode(i.id),
    title: titleFromContent(i.content ?? '', i.id),
  }))
}
